import React, { useEffect, useState } from 'react';
import classnames from 'clsx';
import { get } from 'lodash-es';

import { noop } from 'ui-components/default-props/noop';

import { getMappedSellerProviderList } from './provider-mapping-service';
import { getSortedSellerLabelProviderList } from './provider-sort-service';
import { SellerProviderListItem } from './seller-provider-list-item';
import style from './seller-provider-list.module.css';
import { emptyArray } from 'ui-components/default-props/empty';
import { useProviderServices } from '../../../hooks/use-provider-services';

/** @typedef {import('../../../prop-types/rate-browser-provider').RateBrowserProviderPropType} RateBrowserProvider */

/**
 * Renders a list of seller label providers with their rate information.
 * Auto-selects the first active provider when rates finish loading.
 * @param {object} props
 * @param {string} props.selectedSellerProviderId - ID of the currently selected provider
 * @param {Array<RateBrowserProvider>} props.sellerProviderRates - Rate data for each provider
 * @param {(id: string) => void} props.onSellerProviderClicked - Callback when a provider is clicked
 * @param {string} props.className - Additional CSS class name
 * @param {Array<object>} props.sellerLabelProviders - Provider definitions to display
 * @returns {JSX.Element}
 */
export function SellerProviderList({
  selectedSellerProviderId = '',
  sellerProviderRates = emptyArray,
  onSellerProviderClicked = noop,
  className = '',
  sellerLabelProviders = emptyArray,
}) {
  const providerServices = useProviderServices();
  const mappedSellerProviderList = getMappedSellerProviderList(sellerLabelProviders, sellerProviderRates, providerServices);
  const sortedSellerLabelProviders = getSortedSellerLabelProviderList(mappedSellerProviderList);
  const activeSellerLabelProviders = sortedSellerLabelProviders.filter((sellerProvider) => sellerProvider.status === 'Active');
  const [initialClick, setInitialClick] = useState(false);

  useEffect(() => {
    const someWereLoading = sellerProviderRates.some((rate) => rate.isLoading);
    if (initialClick && !someWereLoading) {
      return;
    }

    if (someWereLoading) {
      setInitialClick(false);
      return;
    }

    if (activeSellerLabelProviders.length) {
      onSellerProviderClicked(activeSellerLabelProviders[0].sellerLabelProviderId);
    }
    setInitialClick(true);
  }, [
    initialClick,
    setInitialClick,
    sellerProviderRates,
    sortedSellerLabelProviders,
    onSellerProviderClicked,
    activeSellerLabelProviders.length,
    activeSellerLabelProviders,
  ]);

  return (
    <div className={classnames(style['seller-provider-list'], className)}>
      {activeSellerLabelProviders.map((mappedSellerProvider) => (
        <SellerProviderListItem
          key={mappedSellerProvider.sellerLabelProviderId}
          className={style['seller-provider-list-item']}
          isLoading={mappedSellerProvider.isLoading}
          name={get(mappedSellerProvider, 'name') || get(mappedSellerProvider, 'providerDisplayInformation.name')}
          numberOfValidRates={mappedSellerProvider.rates?.length ?? 0}
          onClick={onSellerProviderClicked}
          providerId={Number.parseInt(mappedSellerProvider.labelProviderId, 10)}
          selected={selectedSellerProviderId === mappedSellerProvider.sellerLabelProviderId}
          sellerLabelProviderId={mappedSellerProvider.sellerLabelProviderId}
        />
      ))}
    </div>
  );
}
